import React from 'react';
import { css } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import ScoutBubble from '@birdi/icons/scout-bubble.svg';
import ScoutAvatar from '@birdi/icons/ScoutAvatar.svg';
import { useSnapshot } from 'valtio';
import { assistantUi, mapLayout, requestAssistantOpen } from './map-bridge';

/**
 * The Birdi's-AI-Agent entry point — a gradient AI badge that lives in the app
 * headers (cloud top nav and the map header) with an "Open Birdi's AI Agent"
 * tooltip. Hides itself while the panel is open; shows an unread dot when a
 * response finished while the panel was away.
 *
 * Deliberately Redux-free: map routes run a separate Redux store (see
 * map-layout.tsx), so this reads open/unread state and requests opens via
 * the valtio map-bridge, which works under any store — or none.
 */
export const AssistantLauncher: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { open, unread, enabled, suppressed } = useSnapshot(assistantUi);
  // `suppressed`: routes the assistant is switched off on, e.g. Sites
  // (BIR-321) — see isAssistantHiddenPath.
  if (!enabled || suppressed || open) return null;

  return (
    <button
      type="button"
      className={className}
      aria-label={
        unread ? "Open Birdi's AI Agent — new response" : "Open Birdi's AI Agent"
      }
      data-testid="assistant-launcher"
      onClick={() => requestAssistantOpen()}
      css={(theme: BirdiTheme) => css`
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        align-self: center;
        border: none;
        background: transparent;
        cursor: pointer;
        padding: 6px;
        border-radius: 8px;
        transition: background 0.15s ease;
        /* The assistant is disabled on phones — no entry point either. */
        @media (max-width: ${breakpoints.xs}) {
          display: none;
        }
        &:hover {
          background: ${theme.mono10};
        }
        svg {
          width: 34px;
          height: 34px;
        }
        ${unread
          ? `&::before {
               content: '';
               position: absolute;
               top: 2px;
               right: 2px;
               width: 10px;
               height: 10px;
               border-radius: 50%;
               background: ${theme.flora};
               border: 2px solid ${theme.mono0};
               z-index: 1;
             }`
          : ''}
        /* Tooltip */
        &::after {
          content: "Open Birdi's AI Agent";
          position: absolute;
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          background: ${theme.mono0};
          color: ${theme.midnight};
          font-family: inherit;
          font-size: 0.95rem;
          font-weight: 700;
          white-space: nowrap;
          padding: 8px 14px;
          border-radius: 10px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.12s ease;
          pointer-events: none;
          z-index: 1200;
        }
        &:hover::after,
        &:focus-visible::after {
          opacity: 1;
          visibility: visible;
        }
      `}
    >
      <ScoutBubble />
    </button>
  );
};

/**
 * "Ask Birdi's AI Agent" — the right-edge tab entry point: a canary vertical pill
 * fixed to the screen's right side with Scout at the bottom. Hidden while
 * the panel is open, on the Sites routes, and on phones (the assistant is
 * disabled there).
 * Redux-free for the same reason as the launcher above.
 */
export const AssistantSideTab: React.FC = () => {
  const { open, suppressed } = useSnapshot(assistantUi);
  const { mapMounted, mediaViewActive } = useSnapshot(mapLayout);
  if (open) return null;
  // Routes the assistant is switched off on, e.g. Sites (BIR-321). The widget
  // mounts this tab from the app root, so the route check can't live at the
  // call site — see isAssistantHiddenPath.
  if (suppressed) return null;
  // Full media view: the media browser replaces the map and the tab would
  // float over it, so hide it — the map-header launcher stays as the entry
  // point (BIR-214).
  if (mapMounted && mediaViewActive) return null;

  return (
    <button
      type="button"
      aria-label="Ask Birdi's AI Agent"
      data-testid="assistant-side-tab"
      onClick={() => requestAssistantOpen()}
      css={(theme: BirdiTheme) => css`
        position: fixed;
        right: 0;
        top: 40%;
        /* Context-dependent stacking. On a map, sit BEHIND the map's panel
           stacks — the layer toolbox, the right-side inspector and the
           bottom-right controls all render at z-index 10, and the media
           extended view at 50 — so an open toolbox paints over the tab
           (BIR-214). Everywhere else (e.g. the dashboard) there are no such
           panels, so sit above app content. */
        z-index: ${mapMounted ? 9 : 940};
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        /* Cream fill from the design (matches the scout-bubble icon fill). */
        background: #fbe3b0;
        border: 2px solid ${theme.canary};
        border-right: none;
        color: ${theme.midnight};
        padding: 22px 8px 14px;
        border-radius: 14px 0 0 14px;
        box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.12);
        transition: transform 0.15s ease;
        &:hover {
          transform: translateX(-2px);
        }
        @media (max-width: ${breakpoints.xs}) {
          display: none;
        }
      `}
    >
      <span
        css={css`
          writing-mode: vertical-rl;
          transform: rotate(180deg);
          font-family: inherit;
          font-size: 1.05rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          white-space: nowrap;
        `}
      >
        Ask Birdi's AI Agent
      </span>
      <span
        aria-hidden
        css={css`
          display: flex;
          svg {
            width: 34px;
            height: 34px;
            border-radius: 50%;
          }
        `}
      >
        <ScoutAvatar />
      </span>
    </button>
  );
};
